import { useEffect, useState } from 'react';

import { Button, Group, Loader, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy, IconDownload, IconRefresh } from '@tabler/icons-react';

import { REGISTRY_DIGEST_POLL_MAX_MS, useRegistryDigest } from '@/hooks/useRegistryDigest';
import {
  canDigestRegistryOrder,
  type RegistryDeliveredFile,
  type RegistryDigestAddedToReport,
  type RegistryDigestAddToReportPreview,
  type RegistryDigestStatus,
  type RegistryDocumentDigest,
  type RegistryOrder,
  type RegistryProduct,
  type RegistryReportContent,
} from '@/types/registry-documents';
import { toError } from '@/utils/errors';
import { copyDigestMarkdownForWord } from './digestClipboard';
import { describeDigestError } from './digestErrors';
import { writtenDate } from './digestTemplate';
import { AddedToReportLine, AddToReportControl } from './RegistryDigestAddToReport';
import { RegistryDigestView } from './RegistryDigestView';

export { describeDigestError } from './digestErrors';

/** How often the "started n min ago" line is redrawn while a run reads. */
const CLOCK_TICK_MS = 30_000;

/** How long polling waits before calling a run stalled, in the minutes
 * the stalled line names. */
const STALLED_AFTER_MINUTES = Math.round(REGISTRY_DIGEST_POLL_MAX_MS / 60_000);

/** Copy the digest for Word, from the markdown the API renders for the
 * report, and say how it went. */
async function copyForWord(loadMarkdown: () => Promise<string>): Promise<void> {
  try {
    const mode = await copyDigestMarkdownForWord(await loadMarkdown());
    notifications.show({
      title: 'Copied - paste into Word',
      message:
        mode === 'html'
          ? 'The headings and tables keep their layout.'
          : 'Copied as plain text: this browser cannot copy formatted tables.',
      color: 'green',
      icon: <IconCheck size={16} />,
    });
  } catch (err) {
    notifications.show({
      title: 'Could not copy',
      message: toError(err).message,
      color: 'red',
    });
  }
}

/** When the documents were delivered, in the report's written date form:
 * the latest file's day. */
function deliveredDateOf(documents: readonly RegistryDeliveredFile[]): string | null {
  const latest = documents
    .map((file) => file.storedAt)
    .filter((value): value is string => Boolean(value))
    .sort()
    .at(-1);
  return writtenDate(latest);
}

/** Whole minutes since `iso`, never negative; null when unreadable. */
function minutesSince(iso: string | null, now: number): number | null {
  if (!iso) {
    return null;
  }
  const then = new Date(iso).getTime();
  return Number.isNaN(then) ? null : Math.max(0, Math.floor((now - then) / 60_000));
}

/** A clock that ticks while `active`, so a "started n min ago" line keeps
 * up without a re-render from elsewhere. */
function useClock(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

interface RegistryDigestSectionProps {
  order: Pick<RegistryOrder, 'id' | 'companyName'>;
  /** The extraction's state as the surface knows it (the row, the detail). */
  status: RegistryDigestStatus | null | undefined;
  /** The files delivered against the order: what decides whether an
   * extraction is offered, and when the sources say they were retrieved. */
  documents: RegistryDeliveredFile[];
  /** The catalogue lines the order was placed from: the names its
   * products go by, which tell a trace report the desk delivered under
   * another product's code. */
  catalogue: RegistryProduct[];
  /** The report's document, from the order's detail: what decides whether
   * "Add to report" is offered. Omitted or null where the surface has no
   * detail, or the API predates it: the action is then not shown. */
  reportContent?: RegistryReportContent | null;
  /** The digest was inserted into the report: the surface re-reads the
   * order and, where it shows the report, the report. */
  onAddedToReport?: () => void;
}

/**
 * Information extracted from an order's delivered documents, on the order
 * itself, under the files: the button that starts a run, the run as it
 * reads, and the sections once it is done (Corporate Information,
 * Financial Information, a section per other document), with the ways to
 * take them into Word.
 *
 * Shown where the panel is, which is the admin surfaces only. Offered only
 * when a delivered file is one the extraction reads (any PDF but a trace
 * report); a run that exists is shown whatever the files are now.
 */
export function RegistryDigestSection({
  order,
  status,
  documents,
  catalogue,
  reportContent = null,
  onAddedToReport,
}: Readonly<RegistryDigestSectionProps>) {
  const digest = useRegistryDigest(order.id, status);
  return (
    <RegistryDigestSectionView
      companyName={order.companyName}
      digestable={canDigestRegistryOrder({ documents, catalogueSnapshot: catalogue })}
      deliveredAt={deliveredDateOf(documents)}
      status={digest.status}
      digest={digest.digest}
      reportContent={reportContent}
      isStarting={digest.isStarting}
      isLoadingDigest={digest.isLoadingDigest}
      isDownloading={digest.isDownloading}
      isLoadingMarkdown={digest.isLoadingMarkdown}
      isAddingToReport={digest.isAddingToReport}
      isStalled={digest.isStalled}
      error={digest.error}
      onStart={() => void digest.start()}
      onDownloadDocx={() => void digest.downloadDocx()}
      onLoadMarkdown={digest.loadMarkdown}
      onRefresh={() => void digest.refresh()}
      onPreviewAddToReport={digest.previewAddToReport}
      onAddToReport={digest.addToReport}
      onAddedToReport={() => onAddedToReport?.()}
    />
  );
}

export interface RegistryDigestSectionViewProps {
  companyName: string;
  /** A delivered file the extraction reads is on the order. */
  digestable: boolean;
  /** When the documents were delivered, formatted, for the sources. */
  deliveredAt: string | null;
  status: RegistryDigestStatus | null;
  digest: RegistryDocumentDigest | null;
  /** The report's document; null hides "Add to report". */
  reportContent: RegistryReportContent | null;
  isStarting: boolean;
  isLoadingDigest: boolean;
  isDownloading: boolean;
  /** The markdown for the clipboard is being read. */
  isLoadingMarkdown: boolean;
  isAddingToReport: boolean;
  /** Polling gave up with the run still marked running. */
  isStalled: boolean;
  error: Error | null;
  onStart: () => void;
  onDownloadDocx: () => void;
  /** The digest as report markdown, for "Copy for Word"; throws when it
   * cannot be read. */
  onLoadMarkdown: () => Promise<string>;
  onRefresh: () => void;
  /** The dry run of the insertion; null when refused (`error` says why). */
  onPreviewAddToReport: () => Promise<RegistryDigestAddToReportPreview | null>;
  /** The insertion itself; null when refused. */
  onAddToReport: (expectedContentVersion: number) => Promise<RegistryDigestAddedToReport | null>;
  /** The insertion succeeded. */
  onAddedToReport: () => void;
}

/** The section as data in and markup out: one of four states. */
export function RegistryDigestSectionView(props: Readonly<RegistryDigestSectionViewProps>) {
  const { status, digestable } = props;
  if (status === null) {
    return digestable ? <IdleLine {...props} /> : null;
  }
  if (status.status === 'running') {
    return <RunningLine {...props} status={status} />;
  }
  if (status.status === 'failed') {
    return <FailedLine {...props} status={status} />;
  }
  return <CompletedBlock {...props} status={status} />;
}

/** A refusal under the controls. An alert, so a screen reader hears it
 * when it appears: the button it followed has often just gone. */
function ErrorLine({ error }: Readonly<{ error: Error | null }>) {
  if (!error) {
    return null;
  }
  return (
    <Text size="xs" c="red" role="alert" data-testid="registry-digest-error">
      {describeDigestError(error)}
    </Text>
  );
}

function IdleLine({ isStarting, error, onStart }: Readonly<RegistryDigestSectionViewProps>) {
  return (
    <Stack gap={4} data-testid="registry-digest-section">
      <Group gap="xs" justify="space-between" align="center">
        <Text size="xs" c="dimmed">
          Information can be extracted from the delivered documents.
        </Text>
        <Button size="compact-xs" variant="light" loading={isStarting} onClick={onStart}>
          Extract document information
        </Button>
      </Group>
      <ErrorLine error={error} />
    </Stack>
  );
}

function RunningLine({
  status,
  isStalled,
  error,
  onRefresh,
}: Readonly<RegistryDigestSectionViewProps & { status: RegistryDigestStatus }>) {
  const now = useClock(!isStalled);
  const minutes = minutesSince(status.startedAt ?? status.requestedAt, now);
  const since =
    minutes === null
      ? ''
      : minutes < 1
        ? ' started less than a minute ago'
        : ` started ${minutes} min ago`;
  // A status region: the button that started the run is gone, and the
  // loader has no role, so the line is what tells assistive tech the run
  // is reading, or has stalled.
  return (
    <Stack gap={4} data-testid="registry-digest-section">
      {isStalled ? (
        <Group gap="xs" justify="space-between" align="center" role="status">
          <Text size="xs" c="orange">
            Still marked as reading after {STALLED_AFTER_MINUTES} minutes. The run may have been
            interrupted; check again, or try later.
          </Text>
          <Button size="compact-xs" variant="subtle" onClick={onRefresh}>
            Check again
          </Button>
        </Group>
      ) : (
        <Group gap="xs" wrap="wrap" role="status">
          <Loader size="xs" />
          <Text size="xs">Reading the documents…{since}</Text>
          {status.model && (
            <Text size="xs" c="dimmed">
              {status.model}
            </Text>
          )}
        </Group>
      )}
      <ErrorLine error={error} />
    </Stack>
  );
}

function FailedLine({
  status,
  digestable,
  isStarting,
  error,
  onStart,
}: Readonly<RegistryDigestSectionViewProps & { status: RegistryDigestStatus }>) {
  return (
    <Stack gap={4} data-testid="registry-digest-section">
      <Group gap="xs" justify="space-between" align="center">
        <Text size="xs" c="red" role="alert">
          Could not extract document information
          {status.error ? `: ${status.error}` : '.'}
        </Text>
        {digestable && (
          <Button
            size="compact-xs"
            variant="light"
            color="red"
            loading={isStarting}
            onClick={onStart}
          >
            Try again
          </Button>
        )}
      </Group>
      <ErrorLine error={error} />
    </Stack>
  );
}

function CompletedBlock({
  companyName,
  digestable,
  deliveredAt,
  digest,
  status,
  reportContent,
  isStarting,
  isLoadingDigest,
  isDownloading,
  isLoadingMarkdown,
  isAddingToReport,
  error,
  onStart,
  onDownloadDocx,
  onLoadMarkdown,
  onRefresh,
  onPreviewAddToReport,
  onAddToReport,
  onAddedToReport,
}: Readonly<RegistryDigestSectionViewProps & { status: RegistryDigestStatus }>) {
  // The "Add to report" dialog is open: it shows the refusal it meets
  // itself, so the line under the actions does not say it twice.
  const [confirming, setConfirming] = useState(false);

  if (isLoadingDigest) {
    return (
      <Group gap="xs" data-testid="registry-digest-section">
        <Loader size="xs" />
        <Text size="xs" c="dimmed">
          Loading the document information…
        </Text>
      </Group>
    );
  }
  if (!digest) {
    return (
      <Stack gap={4} data-testid="registry-digest-section">
        <Group gap="xs" justify="space-between" align="center">
          <Text size="xs" c="red">
            The document information could not be loaded
            {error ? `: ${describeDigestError(error)}` : '.'}
          </Text>
          <Button size="compact-xs" variant="light" color="red" onClick={onRefresh}>
            Try again
          </Button>
        </Group>
      </Stack>
    );
  }
  return (
    <Stack gap={4} data-testid="registry-digest-section">
      <RegistryDigestView
        digest={digest}
        companyName={companyName}
        deliveredAt={deliveredAt}
        actions={
          <Stack gap={2} align="flex-end">
            <Group gap={4} wrap="wrap">
              <Button
                size="compact-xs"
                variant="light"
                leftSection={<IconCopy size={14} />}
                loading={isLoadingMarkdown}
                onClick={() => void copyForWord(onLoadMarkdown)}
              >
                Copy for Word
              </Button>
              <Button
                size="compact-xs"
                variant="light"
                leftSection={<IconDownload size={14} />}
                loading={isDownloading}
                onClick={onDownloadDocx}
              >
                Download .docx
              </Button>
              <AddToReportControl
                companyName={companyName}
                status={status}
                reportContent={reportContent}
                opened={confirming}
                onOpen={() => setConfirming(true)}
                onClose={() => setConfirming(false)}
                isAddingToReport={isAddingToReport}
                error={error}
                onPreview={onPreviewAddToReport}
                onAdd={onAddToReport}
                onAdded={onAddedToReport}
              />
              {digestable && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  leftSection={<IconRefresh size={14} />}
                  disabled={isStarting}
                  onClick={onStart}
                >
                  Run again
                </Button>
              )}
            </Group>
            <AddedToReportLine status={status} reportContent={reportContent} />
          </Stack>
        }
      />
      {!confirming && <ErrorLine error={error} />}
    </Stack>
  );
}
